import type { PushNudge } from './push-nudge'

/**
 * How often the asks to turn push on come back (docs/PUSH_OPT_IN_PLAN.md).
 *
 * Kept on the device, one key per account: on a phone shared by two accounts
 * one of them saying "never" must not silence the other. A storage that cannot
 * be read counts as never dismissed, and a write that fails holds for this
 * visit only, as with the install card.
 */

const DAY_MS = 24 * 60 * 60 * 1000

/** Each press of the cross hides the card for longer, then for good. */
const CARD_DAYS: Record<'ask' | 'blocked' | 'install', number[]> = {
  ask: [1, 7, 30],
  blocked: [7, 30],
  install: [1, 7, 30],
}
/** A new question (asking → blocked) starts its ladder a week out, not now. */
const CARD_RESTART_DAYS = 7

/** The popups share one budget: at most one every three days... */
const POPUP_GAP_DAYS = 3
/** ...and each "not now" pushes the next one further away, then for good. */
const POPUP_DAYS = [3, 7, 30]

interface CardDismissal {
  state: 'ask' | 'blocked' | 'install'
  count: number
  /** Epoch ms the card comes back at; null once it never does. */
  until: number | null
}

interface PopupBudget {
  count: number
  until: number | null
}

const cardKey = (userId: string) => `push_nudge_card:${userId}`
const popupKey = (userId: string) => `push_nudge_popup:${userId}`

function read<T>(key: string): T | null {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null') as T | null
  } catch {
    return null
  }
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Storage blocked: it holds for this visit only.
  }
}

function later(days: number | undefined, now: number): number | null {
  return days === undefined ? null : now + days * DAY_MS
}

function isAskingCard(nudge: PushNudge): nudge is CardDismissal['state'] {
  return nudge === 'ask' || nudge === 'blocked' || nudge === 'install'
}

/**
 * The card's dismissal as it stands for the question being asked now. A
 * different question than the one dismissed (somebody who closed "turn it on"
 * and then pressed Block) restarts the ladder, a week out.
 */
function currentCard(userId: string, nudge: CardDismissal['state'], now: number): CardDismissal {
  const stored = read<CardDismissal>(cardKey(userId))
  if (stored && typeof stored.count === 'number' && stored.state === nudge) return stored
  if (stored && typeof stored.count === 'number') {
    const restarted = { state: nudge, count: 0, until: now + CARD_RESTART_DAYS * DAY_MS }
    write(cardKey(userId), restarted)
    return restarted
  }
  return { state: nudge, count: 0, until: 0 }
}

export function isPushCardDismissed(userId: string, nudge: PushNudge, now = Date.now()): boolean {
  if (!isAskingCard(nudge)) return false
  const { until } = currentCard(userId, nudge, now)
  return until === null || now < until
}

export function dismissPushCard(userId: string, nudge: PushNudge, now = Date.now()) {
  if (!isAskingCard(nudge)) return
  const { count } = currentCard(userId, nudge, now)
  write(cardKey(userId), { state: nudge, count: count + 1, until: later(CARD_DAYS[nudge][count], now) })
}

function currentPopup(userId: string): PopupBudget {
  const stored = read<PopupBudget>(popupKey(userId))
  return stored && typeof stored.count === 'number' ? stored : { count: 0, until: 0 }
}

export function canShowPushPopup(userId: string, now = Date.now()): boolean {
  const { until } = currentPopup(userId)
  return until !== null && now >= until
}

/** A popup was shown: the next one waits at least `POPUP_GAP_DAYS`. */
export function markPushPopupShown(userId: string, now = Date.now()) {
  const budget = currentPopup(userId)
  write(popupKey(userId), { ...budget, until: Math.max(budget.until ?? 0, now + POPUP_GAP_DAYS * DAY_MS) })
}

/** "Not now", or closed: the next one waits longer, and after three, never. */
export function dismissPushPopup(userId: string, now = Date.now()) {
  const { count } = currentPopup(userId)
  write(popupKey(userId), { count: count + 1, until: later(POPUP_DAYS[count], now) })
}
